import React, { useContext, useEffect, useRef, useState } from 'react';
import GetAccessModal from '@/components/landing/GetAccessModal';
import { ThemeContext } from '@/contexts/ThemeContext';
import { tLanding, type LandingLang, type LandingTranslationKey } from '@/i18n/landingTranslations';
import { LandingFooter } from '@/components/landingPrototype/LandingFooter';
import { AssemblingSquares } from '@/components/landingPrototype/AssemblingSquares';
import { FaqSidebar } from '@/components/landingPrototype/FaqSidebar';
import { LandingTopNav } from '@/components/landingPrototype/LandingTopNav';
import { LandingSeo } from '@/components/seo/LandingSeo';
import { highlightLandingText } from '@/components/landingPrototype/highlightLandingText';

import '@/styles/landingPrototype.css';

const FreeInfrastructureLandingPage: React.FC = () => {
  const { isDark, setIsDark } = useContext(ThemeContext);
  const [isAccessOpen, setIsAccessOpen] = useState(false);
  const [isFaqCollapsed, setIsFaqCollapsed] = useState(true);
  const projectsSectionRef = useRef<HTMLElement | null>(null);
  const [projectsVisible, setProjectsVisible] = useState(false);
  const projectsItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [language, setLanguage] = useState<'EN' | 'RU'>(() => {
    try {
      const stored = (localStorage.getItem('language') || 'en').toLowerCase();
      return stored === 'ru' ? 'RU' : 'EN';
    } catch {
      return 'EN';
    }
  });

  const lang: LandingLang = language === 'RU' ? 'ru' : 'en';
  const t = (key: LandingTranslationKey) => tLanding(lang, key);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  // Projects reveal animation 
  useEffect(() => {
    const el = projectsSectionRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          setProjectsVisible(true);
          projectsItemRefs.current.forEach((itemRef, index) => {
            if (!itemRef) return;
            window.setTimeout(() => {
              itemRef.classList.add('visible');
            }, index * 150);
          });
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleLanguage = () => {
    const next = language === 'EN' ? 'RU' : 'EN';
    setLanguage(next);
    try {
      localStorage.setItem('language', next.toLowerCase());
    } catch {
      // ignore
    }
  };

  return (
    <div
      className={`swarmLandingRoot ${isDark ? '' : 'light-theme'}`}
      style={
        {
          ['--faq-offset' as any]: isFaqCollapsed ? '64px' : '360px',
          ['--page-accent' as any]: '#4bc0c0',
          ['--page-accent-dark' as any]: '#3aa0a0',
        } as React.CSSProperties
      }
    >
      <FaqSidebar t={t} accentColor="#4bc0c0" onCollapsedChange={setIsFaqCollapsed} />
      <LandingSeo
        title={t('freeInfrastructureTitle')}
        description={t('freeInfrastructureSubtitle')}
        path="/for-developers"
        lang={lang}
        breadcrumbs={[
          { name: '🏠 Home', path: '/' },
          { name: '🛠️ For developers', path: '/for-developers' },
        ]}
      />
      <LandingTopNav t={t} />

      <div className="landing-main">
      <div className="page page-free-infrastructure">
        <section className="hero">
          <div className="hero-background">
            <AssemblingSquares />
          </div>
          <div className="container">
            <div className="hero-content">
              <div className="hero-text">
                <h1>{highlightLandingText(t('freeInfrastructureTitle'), ['Infrastructure', 'AI', 'Инфраструктура'])}</h1>
                <h2>{t('freeInfrastructureSubtitle')}</h2>
                <div className="hero-buttons">
                  <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                    {t('getAccess')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <section className="free-infrastructure-focus">
            <div className="container">
            <h2 className="free-infrastructure-focus-title">
              {(() => {
                const title = t('freeInfrastructureFocusTitle');
                const [first, ...rest] = title.split(',');
                if (rest.length === 0) {
                  return highlightLandingText(title, ['Focus on development', 'Сосредоточьтесь на разработке']);
                }
                return (
                  <>
                    <span className="accent-text">{first}</span>, {rest.join(',').trim()}
                  </>
                );
              })()}
            </h2>
              <div className="free-infrastructure-focus-grid">
                <div className="free-infrastructure-focus-card">
                  <div className="free-infrastructure-focus-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="free-infrastructure-focus-card-title">{t('freeInfrastructureFocus1Title')}</h3>
                  <p className="free-infrastructure-focus-desc">{t('freeInfrastructureFocus1Desc')}</p>
                </div>
                <div className="free-infrastructure-focus-card">
                  <div className="free-infrastructure-focus-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="free-infrastructure-focus-card-title">{t('freeInfrastructureFocus2Title')}</h3>
                  <p className="free-infrastructure-focus-desc">{t('freeInfrastructureFocus2Desc')}</p>
                </div>
                <div className="free-infrastructure-focus-card">
                  <div className="free-infrastructure-focus-icon" aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="free-infrastructure-focus-card-title">{t('freeInfrastructureFocus3Title')}</h3>
                  <p className="free-infrastructure-focus-desc">{t('freeInfrastructureFocus3Desc')}</p>
                </div>
              </div>
            </div>
          </section>
        </section>

        <section className="free-infrastructure-projects" ref={projectsSectionRef}>
          <div className="container">
            <h2 className={`free-infrastructure-projects-title ${projectsVisible ? 'visible' : ''}`}>
              {highlightLandingText(t('freeInfrastructureProjectsTitle'), ['projects', 'interested', 'проекты', 'интересны'])}
            </h2>
            <div className="free-infrastructure-projects-items">
              <div className="free-infrastructure-projects-item" ref={el => { projectsItemRefs.current[0] = el; }}>
                <h3 className="free-infrastructure-projects-item-title">
                  {highlightLandingText(t('freeInfrastructureProject1Title'), ['Generative', 'Генеративные'])}
                </h3>
                <p className="free-infrastructure-projects-item-desc">{t('freeInfrastructureProject1Desc')}</p>
              </div>
              <div className={`free-infrastructure-projects-divider-wrapper ${projectsVisible ? 'visible' : ''}`}>
                <div className="free-infrastructure-projects-divider free-infrastructure-projects-divider-title" />
                <div className="free-infrastructure-projects-divider free-infrastructure-projects-divider-desc" />
              </div>
              <div className="free-infrastructure-projects-item" ref={el => { projectsItemRefs.current[1] = el; }}>
                <h3 className="free-infrastructure-projects-item-title">
                  {highlightLandingText(t('freeInfrastructureProject2Title'), ['Applied', 'Прикладные'])}
                </h3>
                <p className="free-infrastructure-projects-item-desc">{t('freeInfrastructureProject2Desc')}</p>
              </div>
              <div className={`free-infrastructure-projects-divider-wrapper ${projectsVisible ? 'visible' : ''}`}>
                <div className="free-infrastructure-projects-divider free-infrastructure-projects-divider-title" />
                <div className="free-infrastructure-projects-divider free-infrastructure-projects-divider-desc" />
              </div>
              <div className="free-infrastructure-projects-item" ref={el => { projectsItemRefs.current[2] = el; }}>
                <h3 className="free-infrastructure-projects-item-title">
                  {highlightLandingText(t('freeInfrastructureProject3Title'), ['Tools', 'for', 'developers', 'Инструменты', 'для', 'разработчиков'])}
                </h3>
                <p className="free-infrastructure-projects-item-desc">{t('freeInfrastructureProject3Desc')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="free-infrastructure-how-to">
          <div className="container">
            <h2 className="free-infrastructure-how-to-title">
              {highlightLandingText(t('freeInfrastructureHowToTitle'), ['How to place a project', 'Как разместить проект', 'Swarmind'])}
            </h2>
            <div className="free-infrastructure-how-to-grid">
              <div className="free-infrastructure-how-to-card">
                <div className="free-infrastructure-how-to-icon" aria-hidden="true">
                  <span>1</span>
                </div>
                <h3 className="free-infrastructure-how-to-card-title">{t('freeInfrastructureHowTo1Title')}</h3>
                <p className="free-infrastructure-how-to-card-desc">{t('freeInfrastructureHowTo1Desc')}</p>
              </div>
              <div className="free-infrastructure-how-to-card">
                <div className="free-infrastructure-how-to-icon" aria-hidden="true">
                  <span>2</span>
                </div>
                <h3 className="free-infrastructure-how-to-card-title">{t('freeInfrastructureHowTo2Title')}</h3>
                <p className="free-infrastructure-how-to-card-desc">{t('freeInfrastructureHowTo2Desc')}</p>
              </div>
              <div className="free-infrastructure-how-to-card">
                <div className="free-infrastructure-how-to-icon" aria-hidden="true">
                  <span>3</span>
                </div>
                <h3 className="free-infrastructure-how-to-card-title">{t('freeInfrastructureHowTo3Title')}</h3>
                <p className="free-infrastructure-how-to-card-desc">{t('freeInfrastructureHowTo3Desc')}</p>
              </div>
              <div className="free-infrastructure-how-to-card">
                <div className="free-infrastructure-how-to-icon" aria-hidden="true">
                  <span>4</span>
                </div>
                <h3 className="free-infrastructure-how-to-card-title">{t('freeInfrastructureHowTo4Title')}</h3>
                <p className="free-infrastructure-how-to-card-desc">{t('freeInfrastructureHowTo4Desc')}</p>
              </div>
            </div>
            <div className="free-infrastructure-how-to-cta">
              <button className="cta-button" onClick={() => setIsAccessOpen(true)} type="button">
                {t('getAccess')}
              </button>
            </div>
          </div>
        </section>
      </div>

      </div>

      <LandingFooter
        t={t}
        language={language}
        onToggleLanguage={toggleLanguage}
        isLightTheme={!isDark}
        onToggleTheme={() => setIsDark(!isDark)}
        onGetAccess={() => setIsAccessOpen(true)}
      />

      <GetAccessModal lang={lang} isOpen={isAccessOpen} onClose={() => setIsAccessOpen(false)} />
    </div>
  );
};

export default FreeInfrastructureLandingPage;

